/**
 * SAFECARE Spending Calculator V2 - Bill-Compliant Rebuild
 * 
 * This calculator models what total national health spending WOULD BE
 * under SAFECARE, not what it "saves" vs the current system.
 * 
 * Key Changes from V1:
 * 1. Uses Medicare cost baseline, not CMS NHE projections
 * 2. Implements bill-specified payment rates (Sec 402)
 * 3. Implements drug price formula (Sec 405: IRP)
 * 4. Admin costs are structural (2%), not a policy lever
 * 5. Malpractice savings are structural, not optional
 * 6. Phase-in based on actual population cohorts (Sec 1005)
 */

import { MedicareBaseline } from '../data/medicareBaseline.js';
import { SAFECARE_BILL_PARAMETERS } from './billParameters.js';
import { projectSpending } from '../calculators/growthRateCalculator.js';
import { compoundingEffectsConfig } from '../config/compoundingEffects.js';
import { getEffectiveAdminOverheadRate, getEffectiveDrugCeilingPricePctOfUS } from './policyMath.js';

/**
 * Calculate SAFECARE spending for a given year
 * This is the NEW baseline - what we WILL spend, not what we "save"
 */
/**
 * Calculate SAFECARE spending for a given year
 * This is the NEW baseline - what we WILL spend, not what we "save"
 */
export function calculateYearSpending(year, baselineData, config, currentGDP, eventMultipliers = {}, structuralOverrides = null) {
    try {
        // Get Medicare cost baseline for this year
        // V3 UPGRADE: Use Dynamic Growth Rate Calculator
        // Instead of relying solely on MedicareBaseline.calculateNationalSpending (static extrapolation),
        // we now project year-over-year using differential growth rates if we are past startYear.

        let medicareBase;

        // Base year (first year of simulation) - use standard static baseline
        // For years 2027+, we really should project from 2026 base using dynamic rates
        // BUT to keep consistent with architecture, let's use the static baseline as the "Pre-Reform" anchor
        // and then Discount it by the SAFECARE efficiency gains.

        // Actually, the user's blueprint says: "Project from base year using SAFECARE growth rates"
        // So we need to fetch the Base Year (2027 start) data, and then project forward.

        const rawStaticBase = MedicareBaseline.calculateNationalSpending(year);

        if (!rawStaticBase) {
            console.error(`[SpendingV2] Medicare baseline returned null for year ${year}`);
            return null;
        }

        // Calculate compounded efficiency multiplier relative to baseline
        // We'll compute what spending WOULD be with SAFECARE growth rates vs Baseline growth rates
        // and apply that ratio to the static baseline.
        // This preserves the absolute dollar scale of MedicareBaseline while applying the delta.

        const startYear = config.startYear || 2027;

        if (year > startYear) { // APPLY SAFECARE GROWTH RATES ALWAYS
            // Calculate ratio: SafeCare Projected / Baseline Projected
            const projectedBaseline = projectSpending(startYear, year, 1.0, 'overall', false, structuralOverrides);
            const projectedSafecare = projectSpending(startYear, year, 1.0, 'overall', true, structuralOverrides);

            // Define ratio for use in other components (like LTSS)
            // If year <= startYear, ratio is 1.0
            const efficiencyRatio = projectedSafecare / projectedBaseline;

            // Category specific ratios
            const hospitalRatio = projectSpending(startYear, year, 1.0, 'hospital', true, structuralOverrides) /
                projectSpending(startYear, year, 1.0, 'hospital', false, structuralOverrides);

            const physicianRatio = projectSpending(startYear, year, 1.0, 'physician', true, structuralOverrides) /
                projectSpending(startYear, year, 1.0, 'physician', false, structuralOverrides);

            const drugRatio = projectSpending(startYear, year, 1.0, 'drug', true, structuralOverrides) /
                projectSpending(startYear, year, 1.0, 'drug', false, structuralOverrides);

            // Apply to static base components
            medicareBase = {
                ...rawStaticBase,
                hospital: rawStaticBase.hospital * hospitalRatio,
                physician: rawStaticBase.physician * physicianRatio,
                drug: rawStaticBase.drug * drugRatio,
                // "Other" services are not modeled with a category-specific growth track in growthRateCalculator.
                // We conservatively scale them with the overall efficiency ratio.
                other: rawStaticBase.other * (projectedSafecare / projectedBaseline),
                // Note: Admin in rawStaticBase is implicit, but we reconstruct it later anyway.
                // We apply the efficiency gains to the underlying service cost growth here.
                // Post-acute usually follows hospital trends
                postAcute: rawStaticBase.postAcute * hospitalRatio,
                totalSpending: rawStaticBase.totalSpending * (projectedSafecare / projectedBaseline) // Rough total adjustment
            };

            // Log the impact
            if (year === startYear + 10) {
                console.log(`[SpendingV2] Efficiency Ratio Year ${year}: ${(projectedSafecare / projectedBaseline).toFixed(4)}`);
            }
        } else {
            medicareBase = rawStaticBase;
        }


        // ========================================================================
        // STEP 1: DETERMINE ENROLLED POPULATION
        // ========================================================================
        // Based on phase-in schedule (Sec 1005)
        const enrollmentRate = calculateEnrollmentRate(year, config);
        // FIX: Ensure population base is correct. medicareBase.population is the FULL US population (if derived from NHE)
        // or just the Medicare population? 
        // MediterraneanBaseline returns NATIONAL spending but bases it on Medicare trends?
        // Let's assume medicareBase.population is the full relevant population universe.
        const enrolledPopulation = medicareBase.population * enrollmentRate;

        // ========================================================================
        // STEP 2: PROVIDER PAYMENT (Sec 402) & BEHAVIORAL ADJUSTMENTS
        // ========================================================================
        // Apply Legal Delay to implementation schedule
        const legalDelay = config.legalChallengeDelay || 0;
        const yearsActive = Math.max(0, year - (config.startYear + legalDelay));

        // Behavioral: Utilization Surge (Newly insured use more care)
        // Decays over 3 years: 100% Year 1, 50% Year 2, 0% Year 3
        const surgeDecay = yearsActive < 3 ? (1 - yearsActive * 0.33) : 0;
        const utilizationMultiplier = 1.0 + ((config.utilizationSurge || 0) * surgeDecay);

        // Behavioral: Provider Consolidation (Market power increases prices)
        const consolidationMultiplier = 1.0 + ((config.providerConsolidationSpeed || 0) * yearsActive);

        // Combined Provider Multiplier
        // Apply transitional corridor for hospitals (120% → 100% over 4 years)
        // Check if corridor array exists to prevent access error
        const hospitalMultiplier = getHospitalPaymentMultiplier(yearsActive) * utilizationMultiplier * consolidationMultiplier * (eventMultipliers.hospital || 1.0);
        const physicianMultiplier = 1.0 * utilizationMultiplier * consolidationMultiplier * (eventMultipliers.physician || 1.0);

        // Hospital spending with transitional corridor & behavioral factors
        const hospitalSpending = medicareBase.hospital * hospitalMultiplier * enrollmentRate;

        // Physician spending at 100% Medicare (no corridor) + factors
        const physicianSpending = medicareBase.physician * physicianMultiplier * enrollmentRate;

        // Post-acute at 100% Medicare + factors
        const postAcuteSpending = medicareBase.postAcute * 1.0 * utilizationMultiplier * enrollmentRate;

        // Other services (DME, other Medicare-covered categories)
        // Modeled as medical services affected by utilization & consolidation.
        const otherMedicalSpending = (medicareBase.other || 0) * 1.0 * utilizationMultiplier * consolidationMultiplier * (eventMultipliers.otherMedical || 1.0) * enrollmentRate;

        // ========================================================================
        // STEP 3: DRUG SPENDING (Sec 405)
        // ========================================================================
        const baseDrugSpending = calculateDrugSpending(medicareBase.drug, enrollmentRate, yearsActive, config);
        const drugSpending = baseDrugSpending * (eventMultipliers.drugs || 1.0);

        // ========================================================================
        // STEP 4: ADMIN COSTS (Bill: Sec 105(c) load cap + Sec 801 Platform + Sec 404 burden reduction)
        // ========================================================================
        // Administrative overhead is applied to the medical services base.
        // IMPORTANT: MedicareBaseline returns category components that are computed from the pre-admin base.
        // Do NOT "strip out" 2% by dividing (that would understate the base).
        const medicalServicesBase =
            hospitalSpending + physicianSpending + postAcuteSpending + otherMedicalSpending + drugSpending;

        const adminOverheadRate = getEffectiveAdminOverheadRate({
            yearsActive,
            enforceAdminSimplification: config.enforceAdminSimplification,
            adminEfficiencyFloor: config.adminEfficiencyFloor
        });

        const adminSpending = medicalServicesBase * adminOverheadRate * (eventMultipliers.admin || 1.0);

        // ========================================================================
        // STEP 5: EXTRA BEHAVIORAL COSTS & MARKET DYNAMICS (Death Spiral)
        // ========================================================================

        // 1. Provider Participation / Balance Billing Cost
        // If participation < 100%, remaining care is "out of network" or balance billed at higher rates
        // Model: +$80B/year baseline impact at 0% participation (scaled)
        const nonParticipation = 1.0 - (config.providerParticipationRate || 1.0);
        const balanceBillingCost = nonParticipation * 80000000000 * enrollmentRate;

        // 2. Rural Hospital Closures (Emergency Transport / Crisis Mgmt)
        // Model: +$25B/year at 8% closure rate (scaled)
        const closureRate = config.ruralHospitalClosureRate || 0;
        const closureCrisisCost = (closureRate / 0.08) * 25000000000;

        // 3. Quality Decline (Complications)
        // Model: +$30B/year at 5% decline (scaled)
        const qualityDecline = config.providerQualityDecline || 0;
        const qualityComplicationCost = (qualityDecline / 0.05) * 30000000000 * enrollmentRate;

        // 4. Market Collapse / Death Spiral (New Integration)
        // If we are modeling a private market alternative (e.g. HR548), add collapse costs
        // Only applies if config explicitly flags 'enableMarketCollapse'
        let marketCollapseCost = 0;
        if (config.enableMarketCollapse) {
            // Crisis stabilization funding (Emergency Reinsurance)
            // Modeled as $100B/year if death spiral index > 1.2
            // We use a simplified proxy here since full market dynamics run in parallel
            const deathSpiralIndex = config.deathSpiralIndex || 1.0;
            if (deathSpiralIndex > 1.15) {
                marketCollapseCost = 50000000000; // $50B initial stabilization
            }
            if (deathSpiralIndex > 1.25) {
                marketCollapseCost = 100000000000; // $100B full bailout
            }
        }

        const extraBehavioralCosts = balanceBillingCost + closureCrisisCost + qualityComplicationCost + marketCollapseCost;

        // ========================================================================
        // STEP 6: MALPRACTICE SAVINGS (Structural, Title IX)
        // ========================================================================
        // Malpractice reform reduces costs in two ways:
        // 1. Direct premium savings (0.4% of provider spending)
        // 2. Defensive medicine reduction (1.5% of total spending)
        const providerSpending = hospitalSpending + physicianSpending + postAcuteSpending;
        const malpracticePremiumSavings = providerSpending * 0.004; // 0.4%
        const defensiveMedicineSavings = providerSpending * 0.015; // 1.5% (50% of 3%) applied to provider services only

        // Apply savings (reduce provider spending)
        const providerSpendingAfterMalpractice = providerSpending - malpracticePremiumSavings - defensiveMedicineSavings;

        // ========================================================================
        // STEP 6: WORKFORCE & EDUCATION COSTS (Title V)
        // ========================================================================
        const tuitionSupportCost = calculateTuitionSupportCost(year, baselineData, config);
        const loanForgivenessCost = calculateLoanForgivenessCost(year, config);

        // ========================================================================
        // STEP 7: MEDICAL DEBT REDEMPTION (Title VII)
        // ========================================================================
        const debtRedemptionCost = calculateDebtRedemptionCost(year, config);

        // ========================================================================
        // STEP 9: NEW BENEFITS (Dental, Vision, LTSS)
        // ========================================================================
        const dentalCost = calculateAdultDentalCost(year, config, enrollmentRate);
        const visionCost = calculateAdultVisionCost(year, config, enrollmentRate);

        // Pass efficiency ratio to LTSS (it is derived from federal baseline which needs efficiency adjustment)
        const currentEfficiencyRatio = (year > startYear) ? (projectSpending(startYear, year, 1.0, 'overall', true, structuralOverrides) / projectSpending(startYear, year, 1.0, 'overall', false, structuralOverrides)) : 1.0;

        const ltssCost = calculateMedicaidLTSSCost(year, baselineData, config, enrollmentRate, currentEfficiencyRatio);

        // Small Innovator Support (Sec 407)
        const smallInnovatorCost = calculateSmallInnovatorSupportCost(year, config);

        // Safety Net & Capital Grants (Sec 601, 604)
        const safetyNetCost = calculateSafetyNetCost(year, config);
        const capitalGrantsCost = calculateCapitalGrantsCost(year, config);


        // ========================================================================
        // TOTAL PROGRAM COST
        // ========================================================================
        const totalProgramCost =
            providerSpendingAfterMalpractice +
            drugSpending +
            otherMedicalSpending +
            adminSpending +
            tuitionSupportCost +
            loanForgivenessCost +
            debtRedemptionCost +
            safetyNetCost +
            capitalGrantsCost +
            dentalCost +
            visionCost +
            ltssCost +
            smallInnovatorCost +
            extraBehavioralCosts; // Added


        // ========================================================================
        // RETURN DETAILED BREAKDOWN
        // ========================================================================
        return {
            year,
            enrollmentRate,
            enrolledPopulation,

            // Medical Services
            hospital: hospitalSpending,
            physician: physicianSpending,
            postAcute: postAcuteSpending,
            drug: drugSpending,
            otherMedical: otherMedicalSpending,
            admin: adminSpending,

            // New Benefits
            dental: dentalCost,
            vision: visionCost,
            ltss: ltssCost,

            // Provider total (after malpractice savings)
            provider: providerSpendingAfterMalpractice,

            // Behavioral / Extra Costs
            extraBehavioralCosts,

            // Program Costs
            tuitionSupport: tuitionSupportCost,
            loanForgiveness: loanForgivenessCost,
            debtRedemption: debtRedemptionCost,
            safetyNet: safetyNetCost,
            capitalGrants: capitalGrantsCost,
            smallInnovatorSupport: smallInnovatorCost,

            // Savings
            malpracticePremiumSavings,
            defensiveMedicineSavings,

            // Total
            totalProgramCost,

            // For dashboard breakdown (scaled values)
            contributionToDashboard: {
                provider: providerSpendingAfterMalpractice + otherMedicalSpending,
                drug: drugSpending,
                admin: adminSpending,
                newBenefits: dentalCost + visionCost + ltssCost,
                behavioral: extraBehavioralCosts // Track separately
            }
        };
    } catch (error) {
        console.error(`[SpendingV2] ERROR in year ${year}:`, error);
        console.error('[SpendingV2] Stack:', error.stack);
        return null;
    }
}

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Calculate enrollment rate based on phase-in schedule (Sec 1005)
 */
export function calculateEnrollmentRate(year, config) {
    // Assuming config.startYear corresponds to the first implementation year (Year 3 of enactment)
    // E.g., if Enactment is 2024, Year 1=2025, Year 2=2026, Year 3=2027 (Implementation Starts)
    const legalDelay = config.legalChallengeDelay ?? 0;
    const implementationYear = (config.startYear ?? 2027) + legalDelay;
    const yearsSinceImplementation = year - implementationYear;

    // Before implementation start
    if (yearsSinceImplementation < 0) return 0;

    // Phase 1 (Years 3 & 4 of enactment / Years 0 & 1 of implementation)
    // Populations: Medicare, Medicaid, CHIP, ACA, Uninsured, 55+, <18
    // Estimate: ~60% of population
    // Phase 1 (Years 0-1): 72% enrolled
    if (yearsSinceImplementation === 0 || yearsSinceImplementation === 1) return 0.72;

    // Phase 2 (Year 2): 85% enrolled
    if (yearsSinceImplementation === 2) return 0.85;

    // Phase 3 (Year 3): 98% enrolled
    if (yearsSinceImplementation === 3) return 0.98;

    // Phase 4 (Year 4+): 100% enrolled
    return 1.00;
}

/**
 * Get hospital payment multiplier based on transitional corridor (Sec 402(c)(4))
 */
function getHospitalPaymentMultiplier(yearsActive) {
    const corridor = SAFECARE_BILL_PARAMETERS.providerPayment.transitionalCorridor;

    if (yearsActive < 0) return 1.0;

    // Find corridor entry by year, not by index (yearsActive is 0-based, corridor.year is 1-based)
    const entry = corridor.find(c => c.year === yearsActive + 1);
    if (entry) {
        return entry.multiplier;
    }

    // After corridor ends
    return 1.0;
}

/**
 * Calculate drug spending with IRP formula (Sec 405)
 */
function calculateDrugSpending(baselineDrugSpending, enrollmentRate, yearsActive, config) {
    // Split into brand vs generic
    // Assumption: 70% brand, 30% generic (by spending)
    const brandShare = 0.70;
    const genericShare = 0.30;

    // CORRECTED: 3-year savings ramp per v2.0 spec
    // Year 0 (1st year): 33% of savings
    // Year 1 (2nd year): 67% of savings
    // Year 2+ (3rd year+): 100% of savings
    const activeYearIndex = yearsActive + 1; // 1-based index for logic
    const savingsRamp = Math.min(activeYearIndex / 3, 1.0);

    const brandSpending = baselineDrugSpending * brandShare;

    // Pricing ceiling logic (Sec 405) implemented at an aggregate level.
    // The bill's structure is a statutory ceiling as min(IRP, DVB), with a fallback to DVB if IRP is
    // unavailable or held invalid. We expose that regime via `drugPricingMode` and apply enforcement
    // realism via `enforceIRP` and `enforceDrugCeilings`.
    const { effectivePricePctOfUS } = getEffectiveDrugCeilingPricePctOfUS(config);
    const targetSavingsPercent = Math.max(0, Math.min(1, 1 - effectivePricePctOfUS));
    const realizedSavingsPercent = targetSavingsPercent * savingsRamp;

    const safecareBrandSpending = brandSpending * (1 - realizedSavingsPercent);
    const safecareGenericSpending = baselineDrugSpending * genericShare; // Generics unchanged

    return (safecareBrandSpending + safecareGenericSpending) * enrollmentRate;
}

/**
 * Calculate tuition support costs (Sec 501A)
 */
function calculateTuitionSupportCost(year, baselineData, config) {
    const workforceRow = baselineData.workforceBaseline?.find(d => d.year === year);
    if (!workforceRow) return 0;

    // Tuition support = (Market tuition - Capped tuition) * Students
    const marketTuition = 60000; // Average medical school tuition
    const cappedTuition = SAFECARE_BILL_PARAMETERS.workforce.tuitionCaps.medicalDegree;
    const supportPerStudent = marketTuition - cappedTuition;

    const totalStudents = workforceRow.medSchoolEnrollment * (config.tuitionCapCoverage ?? 1.0);

    return totalStudents * supportPerStudent * (config.tuitionSupportCoverage ?? 1.0);
}

/**
 * Calculate loan forgiveness costs (Sec 503)
 */
function calculateLoanForgivenessCost(year, config) {
    // Simplified: Assume steady-state participation
    // ~50,000 physicians * $30k/year + 200,000 nurses * $15k/year
    const physicianParticipants = 50000;
    const nurseParticipants = 200000;

    const physicianForgiveness = SAFECARE_BILL_PARAMETERS.workforce.loanForgiveness.physicians;
    const nurseForgiveness = SAFECARE_BILL_PARAMETERS.workforce.loanForgiveness.nursesAllied;

    // Use enforceLoanForgiveness if it exists, otherwise default to 1.0 (full enforcement)
    const enforcementLevel = config.enforceLoanForgiveness ?? 1.0;

    return (physicianParticipants * physicianForgiveness + nurseParticipants * nurseForgiveness) * enforcementLevel;
}

/**
 * Calculate debt redemption cost (Sec 701)
 */
function calculateDebtRedemptionCost(year, config) {
    const legalDelay = config.legalChallengeDelay ?? 0;
    const effectiveStartYear = (config.startYear ?? 2027) + legalDelay;
    const yearsActive = year - effectiveStartYear;

    // Only first 10 years
    if (yearsActive < 0 || yearsActive >= 10) return 0;

    const enforcementLevel = config.enforceDebtRedemption ?? 1.0;

    // Default to the range if not specified closer
    // Bill says $5B - $15B. Configurable.
    const averageFunding = config.debtRedemptionBudget || 5000000000;

    return averageFunding * enforcementLevel;
}

/**
 * Calculate safety net costs (Sec 601-602)
 */
function calculateSafetyNetCost(_year, _config) {
    // Emergency care for non-members
    // Estimated at ~$20B/year
    return 20000000000;
}

/**
 * Calculate capital grants (Sec 604)
 */
function calculateCapitalGrantsCost(year, config) {
    // Bill authorizes $100B/year (2026 dollars) - Adjustable
    // Sec 604: Indexed annually to Producer Price Index (PPI) for construction.
    // We assume a 3.5% annual growth for Construction PPI.

    const baseAmount = config.capitalGrantsAnnual || 100000000000;

    // Index from 2026 base year
    const yearsSinceBase = Math.max(0, year - 2026);
    const ppiConstructionRate = 0.035;

    const inflationFactor = Math.pow(1 + ppiConstructionRate, yearsSinceBase);

    return baseAmount * inflationFactor;
}

/**
 * Calculate Small Innovator Support Grants (Sec 407)
 */
function calculateSmallInnovatorSupportCost(_year, _config) {
    // $500M authorized per year
    return 500000000;
}

/**
 * Calculate Adult Dental Benefit (Standard benefit)
 */
function calculateAdultDentalCost(year, config, enrollmentRate) {
    // E.g. ~260M adults. 60% utilization. $600/year avg cost.
    const adultPop = 260000000;
    const utilization = 0.60;
    const unitCost = 600;

    // Scaled by inflation (simplified 3% trend) and enrollment
    // We assume unitCost is 2026 dollars
    const inflationFactor = Math.pow(1.03, year - 2026);

    return adultPop * utilization * (unitCost * inflationFactor) * enrollmentRate;
}

/**
 * Calculate Adult Vision Benefit (Standard benefit)
 */
function calculateAdultVisionCost(year, config, enrollmentRate) {
    // E.g. ~260M adults. 50% utilization. $200/year avg cost.
    const adultPop = 260000000;
    const utilization = 0.50;
    const unitCost = 200;

    const inflationFactor = Math.pow(1.03, year - 2026);

    return adultPop * utilization * (unitCost * inflationFactor) * enrollmentRate;
}

/**
 * Calculate Medicaid LTSS Expansion (Maintenance of Effort)
 */
function calculateMedicaidLTSSCost(year, baselineData, config, enrollmentRate, efficiencyRatio = 1.0) {
    // Proxy: LTSS is roughly 30% of total Medicaid spending
    // We need to estimate Medicaid baseline for this year
    const baseline = baselineData.federalDebtBaseline.find(d => d.year === year);
    if (!baseline) return 0;

    // Medicaid is approx 40% of federal health outlays (Medicare is biggest)
    // Or we use a trusted figure.
    // Rough Estimator: $800B Medicaid * 30% = $240B
    // Let's use 30% of Federal Health Outlays * 0.40 (Medicaid Share)

    const medicaidShareOfFedHealth = 0.40;
    const ltssShareOfMedicaid = 0.30;

    const estimatedMedicaid = baseline.healthOutlays * medicaidShareOfFedHealth;
    const totalLTSS = estimatedMedicaid * ltssShareOfMedicaid;

    // LTSS population is part of the "Medicaid" cohort which enrolls in Phase 1 (Year 3)
    // So it scales with enrollmentRate generally, but technically 100% of Medicaid enrolls in Phase 1.
    // BUT calculateEnrollmentRate handles the cohort blending. 
    // If enrollmentRate is 0.60 (Phase 1), it captures all Medicaid.
    // So we need to be careful not to undercount if enrollmentRate < 1 but Medicaid IS enrolled.
    // However, for simplicity, we assume LTSS beneficiaries enroll proportional to general enrollment
    // or arguably FASTER.
    // Let's stick to enrollmentRate for consistency unless Phase 1 logic ensures 100% Medicaid.

    // If Year >= StartYear, Medicaid is enrolled (Phase 1).
    // So if enrollmentRate >= 0.60, we assume LTSS is fully active.

    const ltssActive = (enrollmentRate >= 0.72) ? 1.0 : enrollmentRate;

    // Apply SAFECARE efficiency gains to the raw baseline
    return totalLTSS * ltssActive * efficiencyRatio; // Scale down by system efficiency
}

/**
 * Calculate spending projection for all years
 */
export function calculateSpendingProjection(baselineData, config) {
    console.log('[SpendingV2] calculateSpendingProjection called with startYear:', config.startYear);

    const startYear = config.startYear;
    // Use configured years or default to 21 for safety
    const endYear = startYear + (config.simulationYears || 21) - 1;

    const results = [];
    for (let year = startYear; year <= endYear; year++) {
        const yearSpending = calculateYearSpending(year, baselineData, config);
        if (yearSpending) {
            results.push(yearSpending);
        } else {
            console.warn(`[SpendingV2] Year ${year} returned null`);
        }
    }

    console.log(`[SpendingV2] Projection complete: ${results.length} years calculated`);

    return results;
}
